import React, { useState, useEffect, useRef } from 'react';
import IconPicker from './IconPicker';

// Reusable confirm modal — used for any destructive action in this admin panel.
const ConfirmModal = ({ open, isDarkMode, title, message, confirmLabel = 'Delete', cancelLabel = 'Cancel', confirmTone = 'danger', onConfirm, onCancel }) => {
  if (!open) return null;
  const btnColor = confirmTone === 'danger'
    ? 'bg-red-600 hover:bg-red-500'
    : 'bg-purple-600 hover:bg-purple-500';
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" aria-modal="true" role="dialog">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className={`relative w-full max-w-md rounded-2xl shadow-2xl p-6 ${isDarkMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-white text-gray-900'}`}>
        <div className="flex items-start gap-3 mb-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${confirmTone === 'danger' ? 'bg-red-100 text-red-600' : 'bg-purple-100 text-purple-600'}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">{title}</h3>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{message}</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onCancel}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {cancelLabel}
          </button>
          <button onClick={onConfirm}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors ${btnColor}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const ExploreAdmin = ({ API, adminToken, isDarkMode, showNotification, onSettingsUpdate }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}` };

  // Explorer admin state
  const [explorerSettings, setExplorerSettings] = useState({ is_visible: true, is_pro_only: false, quiz_size: 10 });
  const [listenGuessClips, setListenGuessClips] = useState([]);
  const [editingClip, setEditingClip] = useState(null);
  const [newClip, setNewClip] = useState({ audio_url: '', correct_answer: '', darija_text: '', darija_latin: '', option_1: '', option_2: '', option_3: '', difficulty: 'beginner', category: 'general', is_active: true, is_pro: false, order: 0 });
  const [exploreIconUrl, setExploreIconUrl] = useState('');
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [iconSearchQuery, setIconSearchQuery] = useState('');
  const [clipSearchQuery, setClipSearchQuery] = useState('');
  // Admin sub-tab: 'guess' (existing Listen & Guess management) or 'learn' (new Listen & Learn management)
  const [activeManageTab, setActiveManageTab] = useState('guess');
  const [learnSearchQuery, setLearnSearchQuery] = useState('');
  const [learnFilterCategory, setLearnFilterCategory] = useState('all');
  // Listen & Learn add-clip form (no quiz fields)
  const [newLearnClip, setNewLearnClip] = useState({ audio_url: '', english: '', darija_text: '', darija_latin: '', category: 'general', difficulty: 'beginner', is_active: true });
  // New category being added (text input value)
  const [newCategoryName, setNewCategoryName] = useState('');
  // Track which category is being renamed inline (uses category name as key — categories from backend have no id)
  const [editingCategoryKey, setEditingCategoryKey] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  // Track label editor pending state (so user can edit + save)
  const [labelDraft, setLabelDraft] = useState({ learn_heading: '', learn_subheading: '', learn_search_placeholder: '', learn_empty_text: '', guess_heading: '', guess_button_text: '', guess_description: '' });
  // Auto-synced categories (manual + auto-discovered from clips) — loaded from /admin/explore/categories
  const [categoriesList, setCategoriesList] = useState([]);
  // Category icon editor — null = closed; otherwise {name, icon_url, icon} of cat being edited
  const [editingIconCat, setEditingIconCat] = useState(null);
  // Image picker for category icon (separate from the existing explore-button icon picker state)
  const [catIconPickerOpen, setCatIconPickerOpen] = useState(false);

  // Default emoji per category name (fallback when no icon_url and no custom icon)
  const DEFAULT_CAT_EMOJI = {
    'all': '📚', 'greetings': '👋', 'politeness': '🙏', 'quick replies': '💬',
    'about you / understanding': '🤔', 'where / direction': '🗺️', 'when / time': '⏰',
    'what / opinion': '💭', 'asking for help': '🆘', 'commands': '⚡',
    'about myself': '👤', 'about others / descriptions': '👥',
  };
  const getCatDefaultEmoji = (name) => DEFAULT_CAT_EMOJI[(name || '').toLowerCase()] || '📂';
  const renderCatIcon = (cat, size = 24) => {
    if (cat && cat.icon_url) {
      const src = cat.icon_url.startsWith('http') ? cat.icon_url : `${API.replace('/api','')}${cat.icon_url}`;
      return <img src={src} alt="" className="object-contain flex-shrink-0" style={{ width: size, height: size }} />;
    }
    const emoji = (cat && cat.icon) ? cat.icon : getCatDefaultEmoji(cat && cat.name);
    return <span className="flex-shrink-0" style={{ fontSize: size }} aria-hidden="true">{emoji}</span>;
  };

  const saveCategoryIcon = async (name, icon_url, icon) => {
    try {
      const res = await fetch(`${API}/admin/explore/categories/icon`, {
        method: 'PUT',
        headers: {...authHeaders, 'Content-Type': 'application/json'},
        body: JSON.stringify({ name, icon_url: icon_url || '', icon: icon || '' }),
      });
      if (res.ok) {
        showNotification('Icon saved', 'success');
        await loadCategories();
        setEditingIconCat(null);
        setCatIconPickerOpen(false);
      } else showNotification('Failed to save icon', 'error');
    } catch (e) { showNotification('Failed to save icon', 'error'); }
  };
  // Editing a learn clip inline (separate from quiz editingClip to avoid mixing forms)
  const [editingLearnClipId, setEditingLearnClipId] = useState(null);
  const [editingLearnClipDraft, setEditingLearnClipDraft] = useState(null);
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Delete', confirmTone: 'danger' });
  const askConfirm = (cfg) => setConfirmModal({ open: true, confirmLabel: 'Delete', confirmTone: 'danger', ...cfg });
  const closeConfirm = () => setConfirmModal(s => ({ ...s, open: false }));

  // Ref to focus rename input WITHOUT scrolling the page (replaces native autoFocus which scrolls into view)
  const renameInputRef = useRef(null);
  useEffect(() => {
    if (editingCategoryKey && renameInputRef.current) {
      try {
        renameInputRef.current.focus({ preventScroll: true });
        renameInputRef.current.select && renameInputRef.current.select();
      } catch {}
    }
  }, [editingCategoryKey]);

  // Sync labelDraft from settings when settings load
  useEffect(() => {
    if (explorerSettings) {
      setLabelDraft({
        learn_heading: explorerSettings.learn_heading || 'Listen & Learn',
        learn_subheading: explorerSettings.learn_subheading || '',
        learn_search_placeholder: explorerSettings.learn_search_placeholder || 'Search Latin or Arabic...',
        learn_empty_text: explorerSettings.learn_empty_text || 'No audio yet',
        guess_heading: explorerSettings.guess_heading || 'Listen & Guess',
        guess_button_text: explorerSettings.guess_button_text || 'Start Listen & Guess',
        guess_description: explorerSettings.guess_description || 'Hear Darija audio and pick the correct meaning',
      });
    }
  }, [explorerSettings?.updated_at]);

  // Save labels to backend
  const saveLearnLabels = async () => {
    try {
      const res = await fetch(`${API}/admin/explore/settings`, {
        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
        body: JSON.stringify(labelDraft),
      });
      if (res.ok) { showNotification('Labels saved', 'success'); loadData(); }
      else showNotification('Failed to save labels', 'error');
    } catch (e) { showNotification('Failed to save labels', 'error'); }
  };

  // Add a category (uses new auto-sync endpoint)
  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    if (categoriesList.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      showNotification('Category already exists', 'warning');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/explore/categories`, {
        method: 'POST', headers: {...authHeaders, 'Content-Type': 'application/json'},
        body: JSON.stringify({ name }),
      });
      if (res.ok) { setNewCategoryName(''); showNotification('Category added', 'success'); loadCategories(); }
      else showNotification('Failed to add category', 'error');
    } catch (e) { showNotification('Failed to add category', 'error'); }
  };

  // Rename a category (cascades to all clips atomically on the backend)
  const renameCategory = async (oldName) => {
    const newName = editingCategoryName.trim();
    if (!newName) { setEditingCategoryKey(null); return; }
    if (newName === oldName) { setEditingCategoryKey(null); return; }
    try {
      const res = await fetch(`${API}/admin/explore/categories/rename`, {
        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
        body: JSON.stringify({ old_name: oldName, new_name: newName }),
      });
      if (res.ok) {
        const data = await res.json();
        setEditingCategoryKey(null); setEditingCategoryName('');
        showNotification(`Renamed (${data.updated_clips || 0} clip${(data.updated_clips || 0) === 1 ? '' : 's'} updated)`, 'success');
        loadCategories(); loadData();
      } else showNotification('Failed to rename', 'error');
    } catch (e) { showNotification('Failed to rename', 'error'); }
  };

  // Delete a category — blocked by backend if in use; uses confirm modal
  const requestDeleteCategory = (cat) => {
    if ((cat.count || 0) > 0) {
      showNotification(`Cannot delete "${cat.name}" — ${cat.count} clip(s) still use it. Reassign them first.`, 'warning');
      return;
    }
    askConfirm({
      title: 'Delete category?',
      message: `Remove the "${cat.name}" category? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmTone: 'danger',
      onConfirm: async () => {
        closeConfirm();
        try {
          const res = await fetch(`${API}/admin/explore/categories/${encodeURIComponent(cat.name)}`, {
            method: 'DELETE', headers: authHeaders,
          });
          if (res.ok) { showNotification('Category deleted', 'success'); loadCategories(); }
          else {
            const err = await res.json().catch(() => ({}));
            showNotification(err.detail || 'Failed to delete', 'error');
          }
        } catch (e) { showNotification('Failed to delete', 'error'); }
      }
    });
  };

  const loadCategories = async () => {
    try {
      const res = await fetch(`${API}/admin/explore/categories`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setCategoriesList(Array.isArray(data.categories) ? data.categories : []);
      }
    } catch (e) { /* non-critical */ }
  };

  // Add new Listen & Learn audio clip
  const addLearnClip = async () => {
    if (!newLearnClip.audio_url.trim()) { showNotification('Audio URL is required', 'warning'); return; }
    if (!newLearnClip.darija_latin.trim() && !newLearnClip.darija_text.trim()) { showNotification('Provide at least Latin OR Arabic text', 'warning'); return; }
    try {
      const res = await fetch(`${API}/admin/explore/listen-learn/add`, {
        method: 'POST', headers: {...authHeaders, 'Content-Type': 'application/json'},
        body: JSON.stringify(newLearnClip),
      });
      if (res.ok) {
        showNotification('Listen & Learn audio added', 'success');
        setNewLearnClip({ audio_url: '', english: '', darija_text: '', darija_latin: '', category: newLearnClip.category, difficulty: newLearnClip.difficulty, is_active: true });
        loadData(); loadCategories();
      } else showNotification('Failed to add audio', 'error');
    } catch (e) { showNotification('Failed to add audio', 'error'); }
  };

  // Begin editing a learn clip inline
  const startEditLearnClip = (clip) => {
    setEditingLearnClipId(clip.id || clip._id);
    setEditingLearnClipDraft({
      audio_url: clip.audio_url || '',
      english: clip.english || '',
      darija_text: clip.darija_text || '',
      darija_latin: clip.darija_latin || '',
      category: clip.category || 'general',
      difficulty: clip.difficulty || 'beginner',
      is_active: clip.is_active !== false,
    });
  };

  const cancelEditLearnClip = () => {
    setEditingLearnClipId(null);
    setEditingLearnClipDraft(null);
  };

  const saveEditLearnClip = async () => {
    if (!editingLearnClipId || !editingLearnClipDraft) return;
    if (!editingLearnClipDraft.audio_url.trim()) { showNotification('Audio URL is required', 'warning'); return; }
    try {
      const res = await fetch(`${API}/admin/explore/listen-learn/${editingLearnClipId}`, {
        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
        body: JSON.stringify(editingLearnClipDraft),
      });
      if (res.ok) {
        showNotification('Audio updated', 'success');
        cancelEditLearnClip();
        loadData(); loadCategories();
      } else showNotification('Failed to update', 'error');
    } catch (e) { showNotification('Failed to update', 'error'); }
  };

  // Delete a learn clip (uses modal)
  const requestDeleteLearnClip = (clip) => {
    askConfirm({
      title: 'Delete this audio?',
      message: `This will remove the audio clip "${clip.darija_latin || clip.darija_text || clip.id}" permanently. This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmTone: 'danger',
      onConfirm: async () => {
        closeConfirm();
        try {
          const res = await fetch(`${API}/admin/explore/listen-guess/${clip.id || clip._id}`, {
            method: 'DELETE', headers: authHeaders,
          });
          if (res.ok) { showNotification('Audio deleted', 'success'); loadData(); loadCategories(); }
          else showNotification('Failed to delete', 'error');
        } catch (e) { showNotification('Failed to delete', 'error'); }
      }
    });
  };

  // Delete a quiz clip (uses modal — replaces window.confirm)
  const requestDeleteQuizClip = (clip) => {
    askConfirm({
      title: 'Delete this clip?',
      message: `Delete "${clip.correct_answer || clip.id}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmTone: 'danger',
      onConfirm: async () => {
        closeConfirm();
        try {
          const res = await fetch(`${API}/admin/explore/listen-guess/${clip.id || clip._id}`, {
            method: 'DELETE', headers: authHeaders,
          });
          if (res.ok) { showNotification('Clip deleted!', 'success'); loadData(); loadCategories(); }
          else showNotification('Failed to delete', 'error');
        } catch (e) { showNotification('Failed to delete', 'error'); }
      }
    });
  };

  // Load data on mount
  useEffect(() => {
    loadData();
    loadCategories();
  }, []);

  const loadData = async () => {
    try {
      const [expSetRes, expClipsRes] = await Promise.all([
        fetch(`${API}/admin/explore/settings`, { headers: authHeaders }),
        fetch(`${API}/admin/explore/listen-guess`, { headers: authHeaders })
      ]);
      if (expSetRes.ok) { const es = await expSetRes.json(); setExplorerSettings(es); }
      if (expClipsRes.ok) { const ec = await expClipsRes.json(); setListenGuessClips(ec.clips || []); }
      // Load icon from main settings
      try {
        const settingsRes = await fetch(`${API}/settings`);
        if (settingsRes.ok) { const s = await settingsRes.json(); setExploreIconUrl(s.explore_icon_url || ''); }
      } catch {}
    } catch (e) { console.error('Failed to load explorer admin data:', e); }
  };

  return (
                <div className="space-y-6">
                  {/* Explorer Settings */}
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <h4 className={`font-semibold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      🎧 Explore Settings
                    </h4>
                    <div className="space-y-4">
                      {/* Visible Toggle */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Show Explore</p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Show/hide Explore in bottom navigation</p>
                        </div>
                        <button
                          onClick={async () => {
                            const newVal = !explorerSettings.is_visible;
                            setExplorerSettings(prev => ({...prev, is_visible: newVal}));
                            try {
                              await fetch(`${API}/admin/explore/settings`, {
                                method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify({...explorerSettings, is_visible: newVal})
                              });
                              showNotification(newVal ? 'Explore visible' : 'Explore hidden', 'success');
                            } catch (e) { showNotification('Failed to update', 'error'); }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${explorerSettings.is_visible ? 'bg-green-600' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${explorerSettings.is_visible ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {/* Pro Only Toggle */}
                      <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30">
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>💎 Premium Only</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Require Pro subscription for Listen & Guess</p>
                        </div>
                        <button
                          onClick={async () => {
                            const newVal = !explorerSettings.is_pro_only;
                            setExplorerSettings(prev => ({...prev, is_pro_only: newVal}));
                            try {
                              await fetch(`${API}/admin/explore/settings`, {
                                method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify({...explorerSettings, is_pro_only: newVal})
                              });
                              showNotification(newVal ? 'Explore: Pro only' : 'Explore: Free for all', 'success');
                            } catch (e) { showNotification('Failed to update', 'error'); }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${explorerSettings.is_pro_only ? 'bg-purple-600' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${explorerSettings.is_pro_only ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {/* Quiz Size */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Quiz Size</p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Number of clips per quiz session</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={explorerSettings.quiz_size || 10}
                            onChange={(e) => setExplorerSettings(prev => ({...prev, quiz_size: parseInt(e.target.value) || 10}))}
                            className={`w-20 px-2 py-1 rounded border text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                          />
                          <button
                            onClick={async () => {
                              try {
                                await fetch(`${API}/admin/explore/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({quiz_size: explorerSettings.quiz_size || 10})
                                });
                                showNotification('Quiz size updated', 'success');
                              } catch (e) { showNotification('Failed to update', 'error'); }
                            }}
                            className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                      {/* Explorer Button Icon */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Button Icon</p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Custom icon for Explore button on translation page</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {exploreIconUrl ? (
                            <>
                              <img src={`${API.replace('/api', '')}${exploreIconUrl}`} alt="" className="w-8 h-8 object-contain rounded" />
                              <button onClick={async () => {
                                setExploreIconUrl('');
                                try { await fetch(`${API}/admin/settings`, { method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'}, body: JSON.stringify({ explore_icon_url: '' }) }); showNotification('Icon cleared', 'success'); if (onSettingsUpdate) onSettingsUpdate(); } catch {}
                              }} className="text-red-400 text-xs hover:text-red-300">✕</button>
                            </>
                          ) : <span className="text-2xl">🎧</span>}
                          <button onClick={async () => {
                            setIconPickerOpen(true); setIconSearchQuery(''); setAvailableIcons([]);
                            try { const r = await fetch(`${API}/icons?t=${Date.now()}`, { headers: authHeaders, cache: 'no-store' }); if (r.ok) { const d = await r.json(); setAvailableIcons(d.icons || []); } } catch {}
                          }} className={`px-3 py-1 rounded text-xs ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>Pick Icon</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Icon Picker — uses shared IconPicker (browses ALL Image Manager folders) */}
                  {iconPickerOpen && (
                    <IconPicker
                      API={API}
                      adminToken={adminToken}
                      isDarkMode={isDarkMode}
                      onClose={() => setIconPickerOpen(false)}
                      onPick={async (url) => {
                        setExploreIconUrl(url);
                        setIconPickerOpen(false);
                        try { await fetch(`${API}/admin/settings`, { method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'}, body: JSON.stringify({ explore_icon_url: url }) }); showNotification('Explore icon saved!', 'success'); if (onSettingsUpdate) onSettingsUpdate(); } catch {}
                      }}
                    />
                  )}

                  {/* Manage Tabs: Listen & Guess vs Listen & Learn */}
                  <div className={`flex gap-2 p-1 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white shadow-sm border border-gray-200'}`}>
                    <button
                      onClick={() => setActiveManageTab('guess')}
                      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        activeManageTab === 'guess'
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow'
                          : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      🎯 Listen & Guess
                    </button>
                    <button
                      onClick={() => setActiveManageTab('learn')}
                      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        activeManageTab === 'learn'
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow'
                          : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      🎧 Listen & Learn
                    </button>
                  </div>

                  {activeManageTab === 'learn' && (() => {
                    const q = learnSearchQuery.trim().toLowerCase();
                    // Strict separation: L&L tab only shows L&L-only clips
                    const learnClipList = listenGuessClips.filter(c => c.is_learn_only === true);
                    const catNames = categoriesList.map(c => c.name);
                    const filtered = learnClipList.filter(c => {
                      if (learnFilterCategory !== 'all' && (c.category || 'general') !== learnFilterCategory) return false;
                      if (!q) return true;
                      return (c.darija_text || '').toLowerCase().includes(q)
                          || (c.darija_latin || '').toLowerCase().includes(q)
                          || (c.english || '').toLowerCase().includes(q)
                          || (c.category || '').toLowerCase().includes(q);
                    });
                    return (
                      <div className="space-y-4">
                        {/* Editable text labels for the user-facing Listen & Learn page */}
                        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📝 Page Text (shown to users)</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Heading</label>
                              <input type="text" value={labelDraft.learn_heading}
                                onChange={(e) => setLabelDraft({...labelDraft, learn_heading: e.target.value})}
                                placeholder="Listen & Learn"
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Subheading</label>
                              <input type="text" value={labelDraft.learn_subheading}
                                onChange={(e) => setLabelDraft({...labelDraft, learn_subheading: e.target.value})}
                                placeholder="Listen at your own pace"
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Search Placeholder</label>
                              <input type="text" value={labelDraft.learn_search_placeholder}
                                onChange={(e) => setLabelDraft({...labelDraft, learn_search_placeholder: e.target.value})}
                                placeholder="Search Latin or Arabic..."
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Empty State Text</label>
                              <input type="text" value={labelDraft.learn_empty_text}
                                onChange={(e) => setLabelDraft({...labelDraft, learn_empty_text: e.target.value})}
                                placeholder="No audio yet"
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                          </div>
                          <button onClick={saveLearnLabels}
                            className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 text-sm font-medium">
                            💾 Save Page Text
                          </button>
                        </div>

                        {/* Editable text labels for the user-facing Listen & Guess card */}
                        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🎯 Listen & Guess Page Text</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Heading (tab label)</label>
                              <input type="text" value={labelDraft.guess_heading}
                                onChange={(e) => setLabelDraft({...labelDraft, guess_heading: e.target.value})}
                                placeholder="Listen & Guess"
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Button Text</label>
                              <input type="text" value={labelDraft.guess_button_text}
                                onChange={(e) => setLabelDraft({...labelDraft, guess_button_text: e.target.value})}
                                placeholder="Start Listen & Guess"
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                            <div className="md:col-span-2">
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Description</label>
                              <input type="text" value={labelDraft.guess_description}
                                onChange={(e) => setLabelDraft({...labelDraft, guess_description: e.target.value})}
                                placeholder="Hear Darija audio and pick the correct meaning"
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                          </div>
                          <button onClick={saveLearnLabels}
                            className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 text-sm font-medium">
                            💾 Save Page Text
                          </button>
                        </div>

                        {/* Categories — auto-synced from clips + manual */}
                        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📂 Categories ({categoriesList.length})</h4>
                            <button onClick={loadCategories}
                              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-white hover:bg-gray-100 text-gray-600 border border-gray-300'}`}
                              title="Refresh categories">
                              🔄 Refresh
                            </button>
                          </div>
                          <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Categories auto-sync from existing audio clips. Add custom ones below — they show up immediately in the Add Audio form.
                          </p>
                          <div className="flex gap-2 mb-3">
                            <input type="text" value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
                              placeholder="e.g. Greetings, Food, Travel..."
                              className={`flex-1 px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'}`} />
                            <button onClick={addCategory}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 text-sm font-medium whitespace-nowrap">
                              ➕ Add
                            </button>
                          </div>
                          {categoriesList.length === 0 ? (
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No categories yet. Add your first one above, or assign one when you add an audio clip.</p>
                          ) : (
                            <div className="space-y-2">
                              {categoriesList.map(cat => {
                                const key = cat.name;
                                const isEditing = editingCategoryKey === key;
                                const inUse = (cat.count || 0) > 0;
                                return (
                                  <div key={key} className={`flex items-center gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-white border border-gray-200'}`}>
                                    {isEditing ? (
                                      <>
                                        <input type="text" value={editingCategoryName}
                                          ref={renameInputRef}
                                          onChange={(e) => setEditingCategoryName(e.target.value)}
                                          onKeyDown={(e) => { if (e.key === 'Enter') renameCategory(key); if (e.key === 'Escape') setEditingCategoryKey(null); }}
                                          className={`flex-1 px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`} />
                                        <button onClick={() => renameCategory(key)} className="px-2 py-1 bg-purple-600 text-white rounded text-xs">Save</button>
                                        <button onClick={() => setEditingCategoryKey(null)} className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200'}`}>Cancel</button>
                                      </>
                                    ) : (
                                      <>
                                        {renderCatIcon(cat, 28)}
                                        <span className={`flex-1 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{cat.name}</span>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                          {cat.count || 0} clip{(cat.count || 0) === 1 ? '' : 's'}
                                        </span>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                          cat.source === 'manual'
                                            ? (isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700')
                                            : (isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700')
                                        }`}>
                                          {cat.source === 'manual' ? 'custom' : 'from clips'}
                                        </span>
                                        <button
                                          onClick={() => setEditingIconCat({ name: cat.name, icon_url: cat.icon_url || '', icon: cat.icon || '' })}
                                          className="text-sm text-purple-500 hover:text-purple-400" title="Change icon">🎨</button>
                                        <button onClick={() => { setEditingCategoryKey(key); setEditingCategoryName(cat.name); }}
                                          className="text-blue-500 hover:text-blue-400 text-sm" title="Rename">✏️</button>
                                        <button onClick={() => requestDeleteCategory(cat)}
                                          className={`text-sm ${inUse ? 'text-gray-400 cursor-not-allowed' : 'text-red-500 hover:text-red-400'}`}
                                          title={inUse ? `Reassign ${cat.count} clip(s) first` : 'Delete'}
                                          disabled={inUse}>🗑️</button>
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Add new Listen & Learn audio (own form, no quiz fields) */}
                        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>➕ Add Listen & Learn Audio</h4>
                          <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>This audio will appear ONLY in Listen & Learn (not in the quiz).</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Audio URL</label>
                              <input type="text" value={newLearnClip.audio_url}
                                onChange={(e) => setNewLearnClip({...newLearnClip, audio_url: e.target.value})}
                                placeholder="https://www.dropbox.com/..."
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                            <div className="md:col-span-2">
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>English meaning</label>
                              <input type="text" value={newLearnClip.english}
                                onChange={(e) => setNewLearnClip({...newLearnClip, english: e.target.value})}
                                placeholder="hello, how are you?"
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija Latin Script</label>
                              <input type="text" value={newLearnClip.darija_latin}
                                onChange={(e) => setNewLearnClip({...newLearnClip, darija_latin: e.target.value})}
                                placeholder="Salam, kif dayer?"
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Arabic Script</label>
                              <input type="text" value={newLearnClip.darija_text}
                                onChange={(e) => setNewLearnClip({...newLearnClip, darija_text: e.target.value})}
                                placeholder="سلام كيف داير"
                                dir="rtl"
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            </div>
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category</label>
                              <select value={newLearnClip.category}
                                onChange={(e) => setNewLearnClip({...newLearnClip, category: e.target.value})}
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}>
                                {(catNames.length === 0 || !catNames.some(n => n.toLowerCase() === 'general')) && (
                                  <option value="general">general</option>
                                )}
                                {catNames.map(name => (<option key={name} value={name}>{name}</option>))}
                              </select>
                            </div>
                            <div>
                              <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Difficulty</label>
                              <select value={newLearnClip.difficulty}
                                onChange={(e) => setNewLearnClip({...newLearnClip, difficulty: e.target.value})}
                                className={`w-full mt-1 px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}>
                                <option value="beginner">beginner</option>
                                <option value="intermediate">intermediate</option>
                                <option value="advanced">advanced</option>
                              </select>
                            </div>
                          </div>
                          <button onClick={addLearnClip}
                            className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 text-sm font-medium">
                            ➕ Add Audio
                          </button>
                        </div>

                        {/* Library — browse all clips that appear in Listen & Learn */}
                        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🎧 Library ({learnClipList.length} total)</h4>
                          <div className="relative mb-3">
                            <input type="text" value={learnSearchQuery}
                              onChange={(e) => setLearnSearchQuery(e.target.value)}
                              placeholder="🔍 Search Latin / Arabic / category..."
                              className={`w-full pl-4 pr-10 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-purple-500 ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-200 placeholder-gray-400'}`} />
                            {learnSearchQuery && (
                              <button onClick={() => setLearnSearchQuery('')} className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
                            )}
                          </div>
                          {categoriesList.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              <button onClick={() => setLearnFilterCategory('all')}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium ${learnFilterCategory === 'all' ? 'bg-purple-600 text-white' : isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'}`}>All</button>
                              {categoriesList.map(cat => (
                                <button key={cat.name} onClick={() => setLearnFilterCategory(cat.name)}
                                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${learnFilterCategory === cat.name ? 'bg-purple-600 text-white' : isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'}`}>{cat.name}</button>
                              ))}
                            </div>
                          )}
                          {filtered.length === 0 ? (
                            <p className={`text-sm text-center py-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {learnClipList.length === 0 ? 'No audio yet — add your first one above.' : 'No clips match.'}
                            </p>
                          ) : (
                            <div className="space-y-2 max-h-[500px] overflow-y-auto">
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Showing {filtered.length} of {learnClipList.length}</p>
                              {filtered.map(clip => {
                                const cid = clip.id || clip._id;
                                const isEditing = editingLearnClipId === cid;
                                if (isEditing && editingLearnClipDraft) {
                                  return (
                                    <div key={cid} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-purple-500' : 'bg-white border-purple-400'} space-y-2`}>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        <div className="md:col-span-2">
                                          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Audio URL</label>
                                          <input type="text" value={editingLearnClipDraft.audio_url}
                                            onChange={(e) => setEditingLearnClipDraft({...editingLearnClipDraft, audio_url: e.target.value})}
                                            className={`w-full mt-1 px-2 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`} />
                                        </div>
                                        <div className="md:col-span-2">
                                          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>English meaning</label>
                                          <input type="text" value={editingLearnClipDraft.english || ''}
                                            onChange={(e) => setEditingLearnClipDraft({...editingLearnClipDraft, english: e.target.value})}
                                            placeholder="hello, how are you?"
                                            className={`w-full mt-1 px-2 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`} />
                                        </div>
                                        <div>
                                          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija Latin</label>
                                          <input type="text" value={editingLearnClipDraft.darija_latin}
                                            onChange={(e) => setEditingLearnClipDraft({...editingLearnClipDraft, darija_latin: e.target.value})}
                                            className={`w-full mt-1 px-2 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`} />
                                        </div>
                                        <div>
                                          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Arabic Script</label>
                                          <input type="text" dir="rtl" value={editingLearnClipDraft.darija_text}
                                            onChange={(e) => setEditingLearnClipDraft({...editingLearnClipDraft, darija_text: e.target.value})}
                                            className={`w-full mt-1 px-2 py-1.5 border rounded-lg text-sm text-right ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`} />
                                        </div>
                                        <div>
                                          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category</label>
                                          <select value={editingLearnClipDraft.category}
                                            onChange={(e) => setEditingLearnClipDraft({...editingLearnClipDraft, category: e.target.value})}
                                            className={`w-full mt-1 px-2 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}>
                                            {/* Always include current value so a category not yet in the list still shows */}
                                            {editingLearnClipDraft.category && !categoriesList.some(c => c.name === editingLearnClipDraft.category) && (
                                              <option value={editingLearnClipDraft.category}>{editingLearnClipDraft.category}</option>
                                            )}
                                            {(categoriesList.length === 0 || !categoriesList.some(c => c.name.toLowerCase() === 'general')) && (
                                              <option value="general">general</option>
                                            )}
                                            {categoriesList.map(c => (<option key={c.name} value={c.name}>{c.name}</option>))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Difficulty</label>
                                          <select value={editingLearnClipDraft.difficulty}
                                            onChange={(e) => setEditingLearnClipDraft({...editingLearnClipDraft, difficulty: e.target.value})}
                                            className={`w-full mt-1 px-2 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}>
                                            <option value="beginner">beginner</option>
                                            <option value="intermediate">intermediate</option>
                                            <option value="advanced">advanced</option>
                                          </select>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <label className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                          <input type="checkbox" checked={!!editingLearnClipDraft.is_active}
                                            onChange={(e) => setEditingLearnClipDraft({...editingLearnClipDraft, is_active: e.target.checked})} />
                                          Active
                                        </label>
                                        <div className="flex-1" />
                                        <button onClick={saveEditLearnClip}
                                          className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-semibold">
                                          💾 Save
                                        </button>
                                        <button onClick={cancelEditLearnClip}
                                          className={`px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <div key={cid} className={`p-3 rounded-lg border flex items-center gap-3 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-200'}`}>
                                    <button onClick={() => { if (clip.audio_url) { try { new Audio(clip.audio_url).play(); } catch {} } }}
                                      className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 text-white flex items-center justify-center hover:scale-105 transition-transform" aria-label="Play">
                                      <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} dir="rtl">{clip.darija_text || '—'}</p>
                                      <p className={`text-xs truncate font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{clip.darija_latin || '—'}</p>
                                      {clip.english && (
                                        <p className={`text-xs truncate italic ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{clip.english}</p>
                                      )}
                                    </div>
                                    <span className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-bold ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{clip.category || 'general'}</span>
                                    {!clip.is_active && (
                                      <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-100 text-red-700">inactive</span>
                                    )}
                                    <button onClick={() => startEditLearnClip(clip)}
                                      className="text-blue-500 hover:text-blue-400 text-sm flex-shrink-0" title="Edit">✏️</button>
                                    <button onClick={() => requestDeleteLearnClip(clip)}
                                      className="text-red-500 hover:text-red-400 text-sm flex-shrink-0" title="Delete">🗑️</button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  <ConfirmModal
                    open={confirmModal.open}
                    isDarkMode={isDarkMode}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    confirmTone={confirmModal.confirmTone}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={closeConfirm}
                  />

                  {/* Category icon editor modal — emoji input + image picker + clear */}
                  {editingIconCat && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" aria-modal="true" role="dialog">
                      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditingIconCat(null)} />
                      <div className={`relative w-full max-w-md rounded-2xl shadow-2xl p-6 ${isDarkMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-white text-gray-900'}`}>
                        <h3 className="text-lg font-semibold mb-3">🎨 Icon for "{editingIconCat.name}"</h3>
                        <div className={`flex items-center gap-3 p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Current:</span>
                          {renderCatIcon(editingIconCat, 36)}
                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {editingIconCat.icon_url ? '(uploaded image)' : editingIconCat.icon ? '(custom emoji)' : '(default emoji)'}
                          </span>
                        </div>

                        <div className="mb-4">
                          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Emoji (1-2 characters)</label>
                          <input
                            type="text"
                            value={editingIconCat.icon}
                            onChange={(e) => setEditingIconCat({ ...editingIconCat, icon: e.target.value.slice(0, 4), icon_url: '' })}
                            placeholder="👋"
                            className={`w-full mt-1 px-3 py-2 border rounded-lg text-2xl text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                          />
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Type any emoji directly here.</p>
                        </div>

                        <div className="mb-4">
                          <button
                            onClick={async () => {
                              setCatIconPickerOpen(true);
                              setIconSearchQuery('');
                              setAvailableIcons([]);
                              try {
                                const r = await fetch(`${API}/icons?t=${Date.now()}`, { headers: authHeaders, cache: 'no-store' });
                                if (r.ok) { const d = await r.json(); setAvailableIcons(d.icons || []); }
                              } catch {}
                            }}
                            className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium">
                            📁 Pick image from Image Manager
                          </button>
                          {editingIconCat.icon_url && (
                            <p className={`text-xs mt-1 truncate font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {editingIconCat.icon_url}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2 justify-between">
                          <button
                            onClick={() => saveCategoryIcon(editingIconCat.name, '', '')}
                            className={`px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                            Clear (use default)
                          </button>
                          <div className="flex gap-2">
                            <button onClick={() => setEditingIconCat(null)}
                              className={`px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                              Cancel
                            </button>
                            <button
                              onClick={() => saveCategoryIcon(editingIconCat.name, editingIconCat.icon_url, editingIconCat.icon)}
                              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-purple-600 hover:bg-purple-500">
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Image picker for category icons */}
                  {catIconPickerOpen && (
                    <IconPicker
                      API={API}
                      adminToken={adminToken}
                      isDarkMode={isDarkMode}
                      onClose={() => setCatIconPickerOpen(false)}
                      onPick={(url) => {
                        setEditingIconCat((prev) => prev ? { ...prev, icon_url: url, icon: '' } : prev);
                        setCatIconPickerOpen(false);
                      }}
                    />
                  )}

                  {activeManageTab === 'guess' && (
                  <>
                  {/* Add New Clip — new layout: audio top, green left, red right, difficulty below */}
                  <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      ➕ Add New Listen & Guess Clip
                    </h4>

                    {/* Top: Audio URL full-width */}
                    <div className="mb-4">
                      <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Audio URL</label>
                      <input type="text" placeholder="/api/sound-library/play/... or https://..." value={newClip.audio_url}
                        onChange={(e) => setNewClip({...newClip, audio_url: e.target.value})}
                        className={`w-full mt-1 px-3 py-2 border rounded-lg font-mono text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                      />
                    </div>

                    {/* Two-column body: green (correct + Darija) | red (3 wrongs) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        className="p-3 rounded-lg"
                        style={{
                          background: isDarkMode ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.06)',
                          border: `1.5px solid ${isDarkMode ? 'rgba(16,185,129,0.4)' : 'rgba(16,185,129,0.35)'}`,
                        }}
                      >
                        <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>✅ CORRECT</div>
                        <div className="space-y-2">
                          <div>
                            <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Correct Answer (English)</label>
                            <input type="text" placeholder="e.g., Hello" value={newClip.correct_answer}
                              onChange={(e) => setNewClip({...newClip, correct_answer: e.target.value})}
                              className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                          <div>
                            <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija Latin</label>
                            <input type="text" placeholder="e.g., labas" value={newClip.darija_latin}
                              onChange={(e) => setNewClip({...newClip, darija_latin: e.target.value})}
                              className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                          <div>
                            <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija Text (Arabic)</label>
                            <input type="text" placeholder="e.g., لاباس" value={newClip.darija_text} dir="rtl"
                              onChange={(e) => setNewClip({...newClip, darija_text: e.target.value})}
                              className={`w-full mt-1 px-3 py-2 border rounded text-sm text-right ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                        </div>
                      </div>

                      <div
                        className="p-3 rounded-lg"
                        style={{
                          background: isDarkMode ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)',
                          border: `1.5px solid ${isDarkMode ? 'rgba(239,68,68,0.4)' : 'rgba(239,68,68,0.35)'}`,
                        }}
                      >
                        <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>❌ WRONG ANSWERS (3)</div>
                        <div className="space-y-2">
                          <div>
                            <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Wrong 1</label>
                            <input type="text" placeholder="e.g., Goodbye" value={newClip.option_1}
                              onChange={(e) => setNewClip({...newClip, option_1: e.target.value})}
                              className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                          <div>
                            <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Wrong 2</label>
                            <input type="text" placeholder="e.g., Thank you" value={newClip.option_2}
                              onChange={(e) => setNewClip({...newClip, option_2: e.target.value})}
                              className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                          <div>
                            <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Wrong 3</label>
                            <input type="text" placeholder="e.g., I'm hungry" value={newClip.option_3}
                              onChange={(e) => setNewClip({...newClip, option_3: e.target.value})}
                              className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Difficulty (no Category — removed per spec) */}
                    <div className="mt-4">
                      <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Difficulty</label>
                      <select value={newClip.difficulty} onChange={(e) => setNewClip({...newClip, difficulty: e.target.value})}
                        className={`w-full md:w-48 mt-1 px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}>
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4 mt-3">
                      <label className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <input type="checkbox" checked={newClip.is_active} onChange={(e) => setNewClip({...newClip, is_active: e.target.checked})} /> Active
                      </label>
                      <label className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <input type="checkbox" checked={newClip.is_pro} onChange={(e) => setNewClip({...newClip, is_pro: e.target.checked})} /> Pro Only
                      </label>
                      <input type="number" placeholder="Order" value={newClip.order} onChange={(e) => setNewClip({...newClip, order: parseInt(e.target.value) || 0})}
                        className={`w-20 px-2 py-1 border rounded text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                    </div>
                    <button
                      onClick={async () => {
                        if (!newClip.audio_url || !newClip.correct_answer) { showNotification('Audio URL and correct answer are required', 'error'); return; }
                        try {
                          const res = await fetch(`${API}/admin/explore/listen-guess`, {
                            method: 'POST', headers: {...authHeaders, 'Content-Type': 'application/json'},
                            body: JSON.stringify(newClip)
                          });
                          if (res.ok) {
                            showNotification('Clip added!', 'success');
                            setNewClip({ audio_url: '', correct_answer: '', darija_text: '', darija_latin: '', option_1: '', option_2: '', option_3: '', difficulty: 'beginner', category: 'general', is_active: true, is_pro: false, order: 0 });
                            loadData();
                          }
                        } catch (e) { showNotification('Failed to add clip', 'error'); }
                      }}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                    >
                      ➕ Add Clip
                    </button>
                  </div>

                  {/* Listen & Guess Clips List */}
                  <div>
                    <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      🎵 Listen & Guess Clips ({listenGuessClips.filter(c => c.is_learn_only !== true).length})
                    </h3>
                    {listenGuessClips.filter(c => c.is_learn_only !== true).length > 0 && (
                      <div className="relative mb-4">
                        <input
                          type="text"
                          value={clipSearchQuery}
                          onChange={(e) => setClipSearchQuery(e.target.value)}
                          placeholder="🔍 Search Latin / Arabic / answer / category..."
                          className={`w-full pl-4 pr-10 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-purple-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`}
                        />
                        {clipSearchQuery && (
                          <button onClick={() => setClipSearchQuery('')} className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`} aria-label="Clear search">✕</button>
                        )}
                      </div>
                    )}
                    {(() => {
                      const q = clipSearchQuery.trim().toLowerCase();
                      // Strict separation: L&G tab only shows quiz clips (excludes L&L-only)
                      const quizClips = listenGuessClips.filter(c => c.is_learn_only !== true);
                      const filteredClips = q
                        ? quizClips.filter(c =>
                            (c.darija_latin || '').toLowerCase().includes(q) ||
                            (c.darija_text || '').toLowerCase().includes(q) ||
                            (c.correct_answer || '').toLowerCase().includes(q) ||
                            (c.category || '').toLowerCase().includes(q) ||
                            (c.difficulty || '').toLowerCase().includes(q)
                          )
                        : quizClips;
                      if (quizClips.length === 0) {
                        return (
                          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <p>No clips yet. Add your first Listen & Guess clip above.</p>
                          </div>
                        );
                      }
                      if (filteredClips.length === 0) {
                        return (
                          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <p>No clips match "{clipSearchQuery}"</p>
                          </div>
                        );
                      }
                      return (
                      <div className="space-y-3">
                        {q && (
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Showing {filteredClips.length} of {quizClips.length}
                          </p>
                        )}
                        {filteredClips.map((clip) => (
                          <div key={clip.id || clip._id} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                            {editingClip?.id === (clip.id || clip._id) ? (
                              /* Edit Mode — same layout as Add: audio top, green left, red right, difficulty below */
                              <div className="space-y-3">
                                <div>
                                  <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Audio URL</label>
                                  <input type="text" value={editingClip.audio_url}
                                    onChange={(e) => setEditingClip({...editingClip, audio_url: e.target.value})}
                                    className={`w-full mt-1 px-3 py-2 border rounded-lg font-mono text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                  />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div
                                    className="p-3 rounded-lg"
                                    style={{
                                      background: isDarkMode ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.06)',
                                      border: `1.5px solid ${isDarkMode ? 'rgba(16,185,129,0.4)' : 'rgba(16,185,129,0.35)'}`,
                                    }}
                                  >
                                    <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>✅ CORRECT</div>
                                    <div className="space-y-2">
                                      <div>
                                        <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Correct Answer</label>
                                        <input type="text" value={editingClip.correct_answer || ''}
                                          onChange={(e) => setEditingClip({...editingClip, correct_answer: e.target.value})}
                                          className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                                        />
                                      </div>
                                      <div>
                                        <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija Latin</label>
                                        <input type="text" value={editingClip.darija_latin || ''}
                                          onChange={(e) => setEditingClip({...editingClip, darija_latin: e.target.value})}
                                          className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                                        />
                                      </div>
                                      <div>
                                        <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija Text (Arabic)</label>
                                        <input type="text" dir="rtl" value={editingClip.darija_text || ''}
                                          onChange={(e) => setEditingClip({...editingClip, darija_text: e.target.value})}
                                          className={`w-full mt-1 px-3 py-2 border rounded text-sm text-right ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div
                                    className="p-3 rounded-lg"
                                    style={{
                                      background: isDarkMode ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)',
                                      border: `1.5px solid ${isDarkMode ? 'rgba(239,68,68,0.4)' : 'rgba(239,68,68,0.35)'}`,
                                    }}
                                  >
                                    <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>❌ WRONG ANSWERS (3)</div>
                                    <div className="space-y-2">
                                      <div>
                                        <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Wrong 1</label>
                                        <input type="text" value={editingClip.option_1 || ''}
                                          onChange={(e) => setEditingClip({...editingClip, option_1: e.target.value})}
                                          className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                                        />
                                      </div>
                                      <div>
                                        <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Wrong 2</label>
                                        <input type="text" value={editingClip.option_2 || ''}
                                          onChange={(e) => setEditingClip({...editingClip, option_2: e.target.value})}
                                          className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                                        />
                                      </div>
                                      <div>
                                        <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Wrong 3</label>
                                        <input type="text" value={editingClip.option_3 || ''}
                                          onChange={(e) => setEditingClip({...editingClip, option_3: e.target.value})}
                                          className={`w-full mt-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div>
                                  <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Difficulty</label>
                                  <select value={editingClip.difficulty} onChange={(e) => setEditingClip({...editingClip, difficulty: e.target.value})}
                                    className={`w-full md:w-48 mt-1 px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}>
                                    <option value="beginner">Beginner</option>
                                    <option value="intermediate">Intermediate</option>
                                    <option value="advanced">Advanced</option>
                                  </select>
                                </div>

                                <div className="flex gap-2">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const res = await fetch(`${API}/admin/explore/listen-guess/${editingClip.id}`, {
                                          method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                          body: JSON.stringify(editingClip)
                                        });
                                        if (res.ok) { showNotification('Clip updated!', 'success'); setEditingClip(null); loadData(); }
                                      } catch (e) { showNotification('Failed to update', 'error'); }
                                    }}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold"
                                  >
                                    💾 Save
                                  </button>
                                  <button onClick={() => setEditingClip(null)}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm">
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* Display Mode */
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${clip.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      {clip.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    {clip.is_pro && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Pro</span>}
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                      {clip.difficulty}
                                    </span>
                                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>#{clip.order || 0}</span>
                                  </div>
                                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                    ✅ {clip.correct_answer}
                                  </p>
                                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    ❌ {clip.option_1 || '—'} | {clip.option_2 || '—'} | {clip.option_3 || '—'}
                                  </p>
                                  {clip.darija_text && (
                                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                      {clip.darija_text} {clip.darija_latin && `(${clip.darija_latin})`}
                                    </p>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setEditingClip({...clip, id: clip.id || clip._id})}
                                    className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors" title="Edit">
                                    ✏️
                                  </button>
                                  <button
                                    onClick={() => requestDeleteQuizClip(clip)}
                                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors" title="Delete">
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      );
                    })()}
                  </div>
                  </>
                  )}
                </div>
  );
};

export default ExploreAdmin;
